// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  FilterMenuRow,
  VirtualizedNestedFilterMenu,
} from '@/components/virtualized-nested-filter-menu';
import { useStableValue } from '@/hooks/use-stable-value';
import { isSameStringList } from '@/utilities/is-same-string-list';
import { groupBy, isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const DEFAULT_MAX_MENU_HEIGHT_PX = 300;
const UNGROUPED_LABEL = 'Other';
const NO_OPTIONS_MESSAGE = 'No options available';
const NO_MATCHES_MESSAGE = 'No matching options found';

// Menu width is fitted to the longest label so short option lists do not open
// a needlessly wide menu
const WIDTH_PER_CHARACTER_PX = 8;
const WIDTH_PADDING_PX = 100;
const MINIMUM_WIDTH_PX = 280;
const MAXIMUM_WIDTH_PX = 500;

// Typing one of these lists the options a plain label search cannot reach
const SELECTED_SEARCH_TERMS = ['checked', 'selected'];
const ARCHIVED_SEARCH_TERM = 'archived';

/**
 * An option in a searchable filter menu.
 *
 * Deliberately not the row type: a row carries whether it is selected, and this
 * does not. Selection changes on every click while the rest of an option only
 * changes when the data behind it is refetched, so keeping them apart lets a
 * caller memoize this list on its query alone and stop rebuilding every option
 * object each time one checkbox moves.
 */
export interface SearchableFilterOption {
  /**
   * The domain identifier reported back through `onChangeOption`. This menu
   * lists each option once, so it also identifies the row.
   */
  value: string;

  label: string;

  /**
   * Text colour, using MUI's colour system.
   */
  color?: string;

  /**
   * Explanatory text shown in a tooltip beside the label.
   */
  example?: string;

  /**
   * Archived options are hidden until the search matches them, and render
   * with a muted "Archived" suffix when they do. A selected one is always
   * listed, so every selection counted on the button has a row to clear it by.
   */
  isArchived?: boolean;

  /**
   * Groups the option under a heading. Headings appear only when at least one
   * option carries a group.
   */
  group?: string;

  /**
   * Lifts the option above the rest of the list, separated by a divider.
   * Used for entries like "Not set" that are not one of the real options.
   */
  isSpecial?: boolean;
}

interface Props {
  /**
   * The label to display on the button.
   */
  label: string;

  /**
   * The options to display in the menu. A fresh array of unchanged options is
   * held at its last content, so this does not have to be memoized to keep the
   * menu from rebuilding.
   */
  options: SearchableFilterOption[];

  /**
   * The values currently selected.
   */
  selectedValues: string[];

  /**
   * A callback fired when a single option is toggled.
   */
  onChangeOption: (value: string, isActive: boolean) => void;

  /**
   * Placeholder text for the search input.
   */
  searchPlaceholder?: string;

  /**
   * Whether to show the search input.
   *
   * @default true
   */
  enableSearch?: boolean;

  /**
   * Maximum height for the scrolling list.
   *
   * @default 300
   */
  maxHeight?: number;

  /**
   * Whether the options are currently loading.
   */
  isLoading?: boolean;
}

function matchesSearch(
  option: SearchableFilterOption,
  lowerSearch: string,
  isActive: boolean
): boolean {
  if (option.label.toLowerCase().includes(lowerSearch)) {
    return true;
  }

  if (SELECTED_SEARCH_TERMS.includes(lowerSearch) && isActive) {
    return true;
  }

  return lowerSearch === ARCHIVED_SEARCH_TERM && (option.isArchived ?? false);
}

/**
 * A searchable filter menu over a flat or singly grouped list of options.
 * Owns archiving, grouping and the "special" section; the menu chrome,
 * virtualization and search field come from VirtualizedNestedFilterMenu.
 */
export function BaseSearchableFilter(props: Props) {
  // Held at its last content so a caller that rebuilds the array on every
  // render does not rebuild every row with it. Compared deeply rather than
  // field by field: a comparator listing the fields would silently stop
  // reporting a change the day one is added to SearchableFilterOption.
  const options = useStableValue(props.options, isEqual);

  // Callers pass the selection as a fresh literal, so it is held at its last
  // content — which is what lets the row builder below stay memoized
  const selectedValues = useStableValue(props.selectedValues, isSameStringList);

  // A set, not the array: membership is asked once per option per render, and
  // the array form makes that a scan
  const selected = useMemo(() => new Set(selectedValues), [selectedValues]);

  // Read through a ref so a caller's inline handler does not rebuild every row.
  // A row only ever calls it from a click, long after the effect has landed.
  const onChangeOptionRef = useRef(props.onChangeOption);

  useEffect(() => {
    onChangeOptionRef.current = props.onChangeOption;
  });

  const count = useMemo(
    () => options.filter((option) => selected.has(option.value)).length,
    [options, selected]
  );

  const width = useMemo(() => {
    const longestLabel = options.reduce(
      (longest, option) => Math.max(longest, option.label.length),
      0
    );

    return Math.min(
      Math.max(
        longestLabel * WIDTH_PER_CHARACTER_PX + WIDTH_PADDING_PX,
        MINIMUM_WIDTH_PX
      ),
      MAXIMUM_WIDTH_PX
    );
  }, [options]);

  const getRows = useCallback(
    (searchTerm: string): FilterMenuRow[] => {
      const buildOptionRow = (
        option: SearchableFilterOption
      ): FilterMenuRow => ({
        kind: 'option',
        key: `option:${option.value}`,
        label: option.label,
        isActive: selected.has(option.value),
        isArchived: option.isArchived,
        color: option.color,
        example: option.example,
        onChange: (isActive) =>
          onChangeOptionRef.current(option.value, isActive),
      });

      const lowerSearch = searchTerm.trim().toLowerCase();
      // Archived options stay out of the way until they are searched for. A
      // selected one is listed regardless: it is counted on the button, so
      // hiding it leaves a filter the user cannot see or clear.
      const visible = lowerSearch
        ? options.filter((option) =>
            matchesSearch(option, lowerSearch, selected.has(option.value))
          )
        : options.filter(
            (option) => !option.isArchived || selected.has(option.value)
          );

      const special = visible.filter((option) => option.isSpecial);
      const regular = visible.filter((option) => !option.isSpecial);

      const rows: FilterMenuRow[] = special.map(buildOptionRow);

      if (special.length > 0 && regular.length > 0) {
        rows.push({ kind: 'divider', key: 'divider:special' });
      }

      // Truthiness, not a defined check: a blank group is not a group, and
      // grouping on it renders an empty heading
      const hasGroups = regular.some((option) => Boolean(option.group));

      if (!hasGroups) {
        rows.push(...regular.map(buildOptionRow));
      } else {
        const groups = groupBy(
          regular,
          (option) => option.group || UNGROUPED_LABEL
        );

        Object.keys(groups).forEach((group, index) => {
          if (index > 0) {
            rows.push({ kind: 'divider', key: `divider:group:${group}` });
          }

          rows.push({ kind: 'header', key: `header:${group}`, label: group });
          rows.push(...groups[group].map(buildOptionRow));
        });
      }

      if (rows.length === 0) {
        rows.push({
          kind: 'message',
          key: 'no-results',
          text: lowerSearch === '' ? NO_OPTIONS_MESSAGE : NO_MATCHES_MESSAGE,
        });
      }

      return rows;
    },
    [options, selected]
  );

  return (
    <VirtualizedNestedFilterMenu
      label={props.label}
      count={count}
      minWidth={width}
      maxHeight={props.maxHeight ?? DEFAULT_MAX_MENU_HEIGHT_PX}
      isLoading={props.isLoading}
      isSearchEnabled={props.enableSearch}
      searchPlaceholder={
        props.searchPlaceholder ?? `Search ${props.label.toLowerCase()}...`
      }
      getRows={getRows}
    />
  );
}
